import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Layers } from 'lucide-react';
import { EGG_LABELS, EGG_COLORS } from './constants';
import { getInventarioPorLote } from '../../lib/api';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

/**
 * Formatea el código del lote sin duplicar la palabra "Lote". Si el código
 * ya viene con prefijo "Lote " (p. ej. "Lote 2-25"), lo devuelve tal cual;
 * si no, lo antecede.
 */
const formatLoteLabel = (codigo, loteId) => {
  const code = String(codigo || '').trim();
  if (!code) return `Lote ${(loteId || '').slice(0, 6)}`;
  if (/^lote\b/i.test(code)) return code;
  return `Lote ${code}`;
};

/**
 * Obtiene los lotes disponibles para un tipo de huevo dado, tomando en cuenta
 * la clave alternativa "pequeno" ↔ "pequeño" que puede venir del backend.
 */
const getLotesForTipo = (porTipo, tipo) => {
  const alt = tipo === 'pequeño' ? 'pequeno' : (tipo === 'pequeno' ? 'pequeño' : null);
  return porTipo?.[tipo] || (alt && porTipo?.[alt]) || [];
};

/**
 * Tarjetas de existencias por tipo de huevo.
 * Muestra total y precio por unidad + **desglose por lote** (Lote 1: X · Lote 2: Y · Total: Z).
 * Los datos por lote se cargan una vez al montar y se refrescan cuando cambia `stock`.
 */
export const StockOverview = ({ stock, totalStock, precios }) => {
  const [porTipo, setPorTipo] = useState({});

  useEffect(() => {
    let alive = true;
    getInventarioPorLote()
      .then((res) => {
        if (alive) setPorTipo(res.data?.por_tipo || {});
      })
      .catch(() => { /* silencioso — degrada a solo total global */ });
    return () => { alive = false; };
  }, [totalStock]);

  // Suma real de todos los lotes activos de todos los tipos. Cuando el
  // desglose por lote está disponible, ese número es la fuente de verdad
  // (evita inconsistencia entre "total" y suma de tarjetas).
  const totalCalculadoLotes = React.useMemo(() => {
    if (!porTipo || Object.keys(porTipo).length === 0) return null;
    let s = 0;
    Object.values(porTipo).forEach((lotes) => {
      (lotes || []).forEach((l) => { s += Number(l.disponible) || 0; });
    });
    return s;
  }, [porTipo]);
  const totalMostrado = totalCalculadoLotes != null ? totalCalculadoLotes : Number(totalStock || 0);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground tracking-wide">
            EXISTENCIAS EN ALMACÉN
          </h2>
          <p
            className="text-2xl font-bold tabular-nums mt-1"
            style={{ fontFamily: 'Space Grotesk' }}
            data-testid="total-stock"
          >
            {totalMostrado.toLocaleString('en-US')}{' '}
            <span className="text-sm font-normal text-muted-foreground">huevos totales</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(EGG_LABELS).map(([key, label]) => {
          // Mostrar todos los lotes que HAYAN producido este tipo (aunque
          // disponible=0 tras FIFO-inverso), para que el usuario tenga
          // visibilidad completa de todos los lotes activos con producción.
          const lotes = getLotesForTipo(porTipo, key).filter(
            (l) => (Number(l.producido) || 0) > 0 || (Number(l.disponible) || 0) > 0
          );
          // El total del tipo debe COINCIDIR con la suma de los lotes
          // mostrados para evitar que el usuario vea números inconsistentes
          // (ej: card dice 536,869 pero Lote1 + Lote2 suman 537,345).
          // Si el desglose por lote está disponible, esa suma manda; en caso
          // contrario, se cae al total global de `inventario`.
          const sumaLotes = lotes.reduce((s, l) => s + (Number(l.disponible) || 0), 0);
          const totalTipo = lotes.length > 0 ? sumaLotes : Number(stock[key] || 0);
          return (
            <Card
              key={key}
              className={`border ${EGG_COLORS[key]} hover:shadow-md transition-shadow duration-200`}
              data-testid={`stock-card-${key}`}
            >
              <CardContent className="p-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium opacity-80">{label}</p>
                    <p className="text-[10px] opacity-60">
                      {precios[key] > 0 ? `$${Number(precios[key]).toFixed(2)}/u` : 'Sin precio'}
                    </p>
                  </div>
                  <p
                    className="text-2xl font-bold tabular-nums mt-1"
                    style={{ fontFamily: 'Space Grotesk' }}
                    data-testid={`stock-total-${key}`}
                  >
                    {fmt(totalTipo)}
                  </p>
                </div>

                {/* Desglose por lote */}
                {lotes.length > 0 ? (
                  <div className="pt-2 border-t border-current/10 space-y-1.5">
                    <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                      <Layers className="w-3 h-3" /> Por lote
                    </div>
                    <div className="space-y-1">
                      {lotes.map((l) => (
                        <div
                          key={l.lote_id}
                          className="flex items-center justify-between text-xs"
                          data-testid={`stock-lote-${key}-${l.lote_id}`}
                        >
                          <span className="opacity-80 font-mono font-medium">
                            {formatLoteLabel(l.lote_codigo, l.lote_id)}
                          </span>
                          <Badge
                            variant="outline"
                            className="tabular-nums text-[10px] px-1.5 py-0 h-4 bg-background/50"
                          >
                            {fmt(l.disponible)}
                          </Badge>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-1 mt-1 border-t border-current/10 text-xs font-semibold">
                        <span>Total</span>
                        <span className="tabular-nums">{fmt(totalTipo)}</span>
                      </div>
                    </div>
                  </div>
                ) : totalTipo > 0 ? (
                  <p className="text-[10px] opacity-50 pt-1 border-t border-current/10">
                    Sin desglose por lote disponible
                  </p>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};
